import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import { Box, Card, Checkbox, Grid, IconButton, Stack, Tooltip } from '@mui/material';
import isEmpty from 'lodash-es/isEmpty';
import { useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FiMail } from 'react-icons/fi';
import { MdOutlinePhone } from 'react-icons/md';
import { RiPulseFill } from 'react-icons/ri';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ICandidate, IJob, Interaction } from 'wisestep-api';
import {
  WsDetailsProps,
  WsColors,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  WsIconText,
  WsTagsList,
  ChipsType,
  WsButton,
  // timeSince,
  WsIconTag,
  WsIconCalendarOutline,
  WsIconShare,
  timeSince,
  WsDialog,
  IUserDTO,
  WsChip,
  WsChipVariants,
  getDateUsingFormat,
} from 'wisestep-base';

import CandidateStageStatus from '../../../../components/candidateStageStatus';
import { CommunicationLinks } from '../../../../components/CommunicationLinks';
import PulseIconText from '../../../../components/PulseIconText';
import { IBreadCrumbsList } from '../../../../components/WsBreadCrumbs';
import CircularImage from '../../../../components/WsCustomImage/CircularImage';
import { WsTaskCreationCard } from '../../../../components/WsTaskCreationCard';
import { useUserContext } from '../../../../context';
import { SourceType } from '../../../../features/candidates/components/candidateBulkSelectionBar/candidateBulkSelectionBar';
import ErrorLogger from '../../../../providers/ErrorLogger';
import getEncodedQueryParams from '../../../../utils/getEncodedQueryParams';
import { useConversationWidgetStore } from '../../../../utils/useConversationWidgetStore';
import ActionWidget from '../../../candidates/components/actionWidget';
import useCopyToClipboard from '../../../candidates/talentpool/hooks/useCopyToClipboard';
import {
  emailOptions,
  SmallBoxWithRadius,
  phoneOptions,
  getCandidateScoreStatus,
} from '../../../candidates/utils/CandidateHelper';
import MapAsLead from '../../../conversationWidget/components/MapAsLead';
import {
  amplitudeEventJobCrmSmartSourceCalender,
  amplitudeEventJobCrmSmartSourceCard,
  amplitudeEventJobCrmSmartSourceMapAsLead,
  amplitudeEventJobCrmSmartSourceShare,
  amplitudeEventJobCrmSmartSourceTask,
} from '../../utils/AmplitudeEvents';

interface OpenWidgetState {
  open: boolean;
  action: 'email' | 'sms' | 'call' | 'mapping' | 'submission' | 'task';
}

interface JobsCrmCardProps extends WsDetailsProps {
  candidate: ICandidate;
  job?: IJob | null;
  interaction?: Interaction | undefined;
  amplitudeEventHandler?: string;
  isAISmartSourceMapped?: boolean;
  isCvViewed: boolean;
  backwardpathArray?: IBreadCrumbsList[];
}

const JobsCrmCard = ({
  title = '',
  tagsArray = [],
  checkBoxOnclick,
  isActiveCard = true,
  skillsArray = [],
  detailsArray = [],
  rightChildren,
  candidateIndex,
  isSelected = false,
  candidate,
  job = null,
  onActionClick,
  interaction,
  amplitudeEventHandler,
  onOpenShareIcon,
  isAISmartSourceMapped,
  isCvViewed,
  backwardpathArray = [],
}: JobsCrmCardProps) => {
  const [openMapping, setOpenMapping] = useState(false);
  const [showMoreEmails, setShowMoreEmails] = useState(false);
  const [showMoreContact, setShowMoreContact] = useState(false);
  const [, copy] = useCopyToClipboard();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const setSelectedEmail = useConversationWidgetStore((state) => state.updateSelectedEmail);
  const setSelectedPhoneNumber = useConversationWidgetStore(
    (state) => state.updateSelectedPhoneNumber
  );
  const [openJobSelectionWidget, setOpenJobSelectionWidget] = useState<OpenWidgetState>({
    open: false,
    action: 'email',
  });
  const searchQuery = useConversationWidgetStore((state) => state.searchQuery);
  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const status = useMemo(() => {
    if (isAISmartSourceMapped) {
      return 'Mapped';
    } else if (isCvViewed) {
      return 'Viewed';
    }
  }, [isCvViewed, isAISmartSourceMapped]);

  const openWidget = (field: string, value: string) => {
    switch (field) {
      case 'Send SMS':
        setSelectedPhoneNumber(value);
        onActionClick && candidateIndex != undefined && onActionClick(candidateIndex);
        // setOpenJobSelectionWidget({ open: true, action: 'sms' });
        navigate(
          `/candidate-layout/profile?id=${
            candidate.id
          }&array=${[]}&action=sms&backwardpathArray=${getEncodedQueryParams(
            JSON.stringify(backwardpathArray)
          )}`
        );
        if (amplitudeEventHandler === 'AI_Suggestions')
          amplitudeEventJobCrmSmartSourceCard['AI_Send_SMS'](loggedInUser);
        else if (amplitudeEventHandler === 'Similar_Jobs')
          amplitudeEventJobCrmSmartSourceCard['Similar_Send_SMS'](loggedInUser);
        break;
      case 'Call':
        setSelectedPhoneNumber(value);
        onActionClick && candidateIndex != undefined && onActionClick(candidateIndex);
        // setOpenJobSelectionWidget({ open: true, action: 'call' });
        navigate(
          `/candidate-layout/profile?id=${
            candidate.id
          }&array=${[]}&backwardpathArray=${getEncodedQueryParams(
            JSON.stringify(backwardpathArray)
          )}`
        );
        if (amplitudeEventHandler === 'AI_Suggestions')
          amplitudeEventJobCrmSmartSourceCard['AI_Call'](loggedInUser);
        else if (amplitudeEventHandler === 'Similar_Jobs')
          amplitudeEventJobCrmSmartSourceCard['Similar_Call'](loggedInUser);
        break;
      case 'Send Email':
        setSelectedEmail(value);
        onActionClick && candidateIndex != undefined && onActionClick(candidateIndex);
        // setOpenJobSelectionWidget({ open: true, action: 'email' });
        navigate(
          `/candidate-layout/profile?id=${
            candidate.id
          }&array=${[]}&action=email&backwardpathArray=${getEncodedQueryParams(
            JSON.stringify(backwardpathArray)
          )}`
        );
        if (amplitudeEventHandler === 'AI_Suggestions')
          amplitudeEventJobCrmSmartSourceCard['AI_Send_Email'](loggedInUser);
        else if (amplitudeEventHandler === 'Similar_Jobs')
          amplitudeEventJobCrmSmartSourceCard['Similar_Send_Email'](loggedInUser);
        break;
      case 'Copy':
        copy(value);
        if (amplitudeEventHandler === 'AI_Suggestions')
          amplitudeEventJobCrmSmartSourceCard['AI_Copy_Email'](loggedInUser);
        else if (amplitudeEventHandler === 'Similar_Jobs')
          amplitudeEventJobCrmSmartSourceCard['Similar_Copy_Email'](loggedInUser);
        break;
    }
  };
  const url = `/candidate-layout/profile?id=${candidate.id}&array=${[
    candidate.id,
  ]}&backwardpathArray=${getEncodedQueryParams(JSON.stringify(backwardpathArray))}`;
  return (
    <>
      <Card>
        <Box p={2}>
          <Grid container item xs={12} id="job-smart-source-card">
            <Grid item xs={8}>
              <Box pl={1}>
                <Grid container xs={12}>
                  <Grid container alignItems="center">
                    <Grid item>
                      <Checkbox
                        sx={{ p: 0, pointerEvents: 'auto' }}
                        onClick={checkBoxOnclick}
                        size="small"
                        checked={isSelected}
                      />
                    </Grid>
                    <Grid item>
                      <Grid container columnGap={0.5}>
                        <Box px={1}>
                          <CircularImage src={candidate?.profileImage ?? ''} />
                        </Box>
                        <Box>
                          <RouterLink to={url} style={{ textDecoration: 'none' }}>
                            <WsTypography
                              variant={WsTypoVariants.TITLE}
                              color={isActiveCard ? WsTypoColors.BLACK : WsTypoColors.SECONDARY}
                              cursor="pointer"
                              onClick={() => {
                                sessionStorage.setItem('scrollPosition', window.scrollY.toString());
                                sessionStorage.setItem(
                                  'jobsUrls',
                                  `/jobs-crm/${job?.id}/false/0/smartSource${
                                    location.search
                                      ? `${location.search}&redirection=true`
                                      : '?redirection=true'
                                  }`
                                );
                              }}
                            >
                              {title}
                            </WsTypography>
                          </RouterLink>
                          <PulseIconText
                            hover={
                              candidate.createdOn || candidate.modifiedOn
                                ? getDateUsingFormat(
                                    candidate.modifiedOn ?? candidate.createdOn ?? '',
                                    'D MMMM YYYY, HH:mm'
                                  )
                                : ''
                            }
                            text={`Last Updated  ${timeSince(
                              String(candidate.modifiedOn ?? candidate.createdOn)
                            )} by 
                                          ${candidate.modifiedBy ?? candidate.createdBy ?? ''}`}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={4} container alignItems="center" justifyContent="flex-end">
              {status && (
                <Box>
                  <WsChip
                    tooltipText="Candidate Status"
                    variant={WsChipVariants.PRIMARY}
                    isPointer={false}
                    value={status}
                  />
                </Box>
              )}
              {candidate.responseData &&
                Number(candidate.responseData.score) > 1 &&
                getCandidateScoreStatus(
                  Number(candidate.responseData?.score),
                  Number(candidate.responseData?.maxScore),
                  false,
                  true,
                  true
                )}
              <Tooltip title="Calendar">
                <IconButton
                  onClick={() => {
                    if (amplitudeEventHandler)
                      amplitudeEventJobCrmSmartSourceCalender[amplitudeEventHandler](loggedInUser);
                  }}
                >
                  <WsIconCalendarOutline color={WsColors.TertiaryShades500} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Task">
                <IconButton>
                  <TaskOutlinedIcon
                    sx={{ color: WsColors.TertiaryShades500 }}
                    onClick={() => {
                      setOpen(!open);
                      if (amplitudeEventHandler)
                        amplitudeEventJobCrmSmartSourceTask[amplitudeEventHandler](loggedInUser);
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Share">
                <IconButton
                  onClick={() => {
                    onOpenShareIcon && onOpenShareIcon(candidate);
                    if (amplitudeEventHandler)
                      amplitudeEventJobCrmSmartSourceShare[amplitudeEventHandler](loggedInUser);
                  }}
                >
                  <WsIconShare color={WsColors.TertiaryShades500} />
                </IconButton>
              </Tooltip>
            </Grid>
            <WsDialog openDialog={open}>
              <WsTaskCreationCard closeDialog={() => setOpen(false)} />
            </WsDialog>
          </Grid>
          <Grid item xs={12} container>
            <Grid>
              <Stack direction="row" spacing={2} pb={1} pt={1}>
                {candidate.email && (
                  <CommunicationLinks
                    type="email"
                    icon={<FiMail size={14} color={WsColors.TheBlues500} />}
                    emailOrPhNo={candidate.email}
                    objects={emailOptions}
                    onActionClick={openWidget}
                  />
                )}
                {!isEmpty(candidate.alternateEmails) && (
                  <>
                    {showMoreEmails ? (
                      <>
                        {candidate.alternateEmails.map((email, index) => (
                          <CommunicationLinks
                            type="email"
                            key={index}
                            icon={<FiMail size={14} color={WsColors.TheBlues500} />}
                            emailOrPhNo={email}
                            objects={emailOptions}
                            onActionClick={openWidget}
                          />
                        ))}
                      </>
                    ) : (
                      <SmallBoxWithRadius>
                        <Grid
                          item
                          container
                          xs={12}
                          alignItems="center"
                          columnGap={0.5}
                          sx={{ cursor: 'pointer' }}
                        >
                          <WsTypography
                            variant={WsTypoVariants.BODY1}
                            color={WsTypoColors.PRIMARY}
                            onClick={() => setShowMoreEmails(true)}
                            cursor="pointer"
                          >
                            {`+${candidate.alternateEmails.length} More`}
                          </WsTypography>
                        </Grid>
                      </SmallBoxWithRadius>
                    )}
                  </>
                )}
                {candidate.phoneNumber && (
                  <CommunicationLinks
                    type="phone"
                    icon={<MdOutlinePhone size={16} color={WsColors.TheBlues500} />}
                    emailOrPhNo={candidate.phoneNumber}
                    objects={phoneOptions}
                    onActionClick={openWidget}
                  />
                )}
                {!isEmpty(candidate.alternatePhoneNumbers) && (
                  <>
                    {showMoreContact ? (
                      <>
                        {candidate.alternatePhoneNumbers.map((phone, index) => (
                          <CommunicationLinks
                            type="phone"
                            key={index}
                            icon={<MdOutlinePhone size={16} color={WsColors.TheBlues500} />}
                            emailOrPhNo={phone}
                            objects={phoneOptions}
                            onActionClick={openWidget}
                          />
                        ))}
                      </>
                    ) : (
                      <SmallBoxWithRadius>
                        <Grid
                          item
                          container
                          xs={12}
                          alignItems="center"
                          columnGap={0.5}
                          sx={{ cursor: 'pointer' }}
                        >
                          <WsTypography
                            variant={WsTypoVariants.BODY1}
                            color={WsTypoColors.PRIMARY}
                            onClick={() => setShowMoreContact(true)}
                            cursor="pointer"
                          >
                            {`+${candidate.alternatePhoneNumbers.length} More`}
                          </WsTypography>
                        </Grid>
                      </SmallBoxWithRadius>
                    )}
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
          {rightChildren && (
            <Grid item container xs={12}>
              <Box pr={1}>
                <RiPulseFill color={WsColors.TheBlues500} cursor="pointer" />
              </Box>
              {rightChildren}
            </Grid>
          )}
          <Grid container item xs={12} pl={1} py={1} spacing={1}>
            {detailsArray?.map((detailsObj, index) => (
              <Grid item xs={3} key={index}>
                <WsIconText
                  icon={detailsObj.icon ?? <></>}
                  text={detailsObj.labelName ?? ''}
                  popOverText={`${detailsObj.popOverText}`}
                  opacity={true}
                  textCursor="pointer"
                />
              </Grid>
            ))}
          </Grid>
          {!isEmpty(skillsArray) && (
            <Grid container item xs={12} pt={1} alignItems="center">
              <Box pt={0.5} pr={0.6}>
                <WsIconTag />
              </Box>
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                color={WsTypoColors.PRIMARY}
                cursor="pointer"
              >
                Skills :
              </WsTypography>
              <WsTagsList
                chipArray={skillsArray}
                count={10}
                type={ChipsType.PRIMARY}
                borderColorFlag={true}
                query={searchQuery ?? ''}
              />
            </Grid>
          )}
          {!isEmpty(tagsArray) && (
            <Grid container item xs={12} pt={1} alignItems="center">
              <Box pt={0.5} pr={0.6}>
                <WsIconTag />
              </Box>
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                color={WsTypoColors.PRIMARY}
                cursor="pointer"
              >
                Tags :
              </WsTypography>
              <WsTagsList chipArray={tagsArray} count={3} type={ChipsType.SECONDARY} />
            </Grid>
          )}
        </Box>
        {interaction && !candidate.isInDnc && <CandidateStageStatus interaction={interaction} />}
        <Box sx={{ backgroundColor: WsColors.TheBlues25 }} p={1.5}>
          <Stack direction="row">
            <Grid item xs={8} pt={0.6}>
              {/* <FormControlLabel
              control={
                <Box pl={1}>
                  <RiPulseFill color={WsColors.TheBlues500} />
                </Box>
              }
              label={
                <Box pl={1}>
                  <WsTypography
                    variant={WsTypoVariants.BODY2}
                    color={WsTypoColors.BLACK}
                    cursor="pointer"
                  >
                    {`Madhu sent an email ${timeSince(String(Date.now()))}`}
                  </WsTypography>
                </Box>
              }
            /> */}
            </Grid>
            <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
              <Grid item xs={4} container justifyContent="flex-end">
                <Stack direction="row" spacing={2}>
                  <WsButton
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      setOpenMapping(true);
                      if (amplitudeEventHandler)
                        amplitudeEventJobCrmSmartSourceMapAsLead[amplitudeEventHandler](
                          loggedInUser
                        );
                    }}
                  >
                    Map as Lead
                  </WsButton>
                </Stack>
              </Grid>
            </ErrorBoundary>
          </Stack>
        </Box>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          {openMapping && (
            <MapAsLead
              candidate={candidate}
              job={job}
              close={() => setOpenMapping(false)}
              hideJobSelection={true}
              isFrom={
                amplitudeEventHandler === 'AI_Suggestions'
                  ? SourceType.JOBCRM_SMART_SOURCE_AI_SUGGESTIONS
                  : SourceType.JOBCRM_SMART_SOURCE_SIMILAR_JOBS
              }
            />
          )}
        </ErrorBoundary>
      </Card>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        {openJobSelectionWidget.open && candidate?.id && (
          <ActionWidget
            onSelect={() =>
              setOpenJobSelectionWidget({ open: false, action: openJobSelectionWidget.action })
            }
            fromPage={'mapping'}
            action={openJobSelectionWidget.action}
            candidateId={candidate?.id}
          />
        )}
      </ErrorBoundary>
    </>
  );
};

export default JobsCrmCard;
